import { FileCheck } from "lucide-react";
import GenericPracticePage from "@/components/GenericPracticePage";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useLanguage } from "@/hooks/useLanguage";
import { immigrationContent } from "@/i18n/immigration-content";

const Waivers = () => {
  const { language } = useLanguage();
  const content = immigrationContent.waivers[language];

  const faqs = content.faq || [
    {
      question: "What is extreme hardship?",
      answer: "Extreme hardship goes beyond the normal hardship of family separation. It includes serious medical conditions, financial devastation, inability to relocate due to family or country conditions, and other extraordinary circumstances. We help document and present your hardship evidence effectively.",
    },
    {
      question: "How long does waiver processing take?",
      answer: "Processing times vary by waiver type and USCIS workload. I-601A waivers typically take 8-16 months. I-601 waivers processed abroad may take 6-12 months. We monitor your case closely and expedite when possible.",
    },
  ];

  const mainContent = (
    <>
      <div className="prose prose-lg max-w-none mb-12">
        <h2 className="text-3xl font-bold mb-6">{content.sections[0].title}</h2>
        <p className="text-xl text-muted-foreground mb-8">
          {content.sections[0].content}
        </p>

        <h3 className="text-2xl font-bold mb-4">Types of Waivers</h3>
        <ul className="list-disc pl-6 mb-6">
          <li><strong>I-601 Waiver:</strong> For those outside the U.S. or in removal proceedings</li>
          <li><strong>I-601A Provisional Waiver:</strong> Allows you to apply before leaving the U.S. for consular processing</li>
          <li><strong>I-212 Waiver:</strong> Permission to reapply for admission after deportation or removal</li>
          <li><strong>212(h) Waiver:</strong> For certain criminal grounds of inadmissibility</li>
        </ul>

        <h3 className="text-2xl font-bold mb-4">Building Your Case</h3>
        <p className="mb-8">
          Waiver cases require demonstrating extreme hardship to qualifying U.S. citizen or LPR relatives. We gather medical records, psychological evaluations, financial documents, country condition reports, and personal declarations to build the strongest possible case.
        </p>

        <h3 className="text-2xl font-bold mb-6">Frequently Asked Questions</h3>
        <Accordion type="single" collapsible className="w-full">
          {faqs.map((faq, index) => (
            <AccordionItem key={index} value={`item-${index}`}>
              <AccordionTrigger className="text-left">{faq.question}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground">{faq.answer}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </>
  );

  return (
    <GenericPracticePage
      title={content.title}
      subtitle={content.subtitle}
      icon={<FileCheck className="h-20 w-20 mx-auto mb-6 text-accent" />}
      mainContent={mainContent}
    />
  );
};

export default Waivers;